/**
 * Shared types used across all dashboard surface contracts. Per D-07.
 *
 * Schema-first approach (D-01 discretion): Zod schemas are the single source of truth.
 * Types are derived via z.infer<typeof Schema>. These derived types ARE the TypeScript
 * interfaces referenced in FOUND-01/D-03 -- structurally identical, with the added
 * guarantee that type and runtime validator never drift apart.
 *
 * Object strictness policy: All object schemas use .passthrough() by default.
 * Rationale: The backend may evolve with new fields (e.g., adding metadata, feature flags).
 * Strict schemas would cause contract tests to fail on harmless additions, creating
 * false negatives. Contracts validate that REQUIRED fields exist with correct types --
 * they do not reject unknown bonus fields. Use .strict() only where field set is
 * genuinely closed (e.g., enum-like objects).
 *
 * Timestamp convention: All timestamp string fields use TimestampSchema (ISO 8601)
 * or are annotated as human-readable display strings. See TimestampSchema below.
 */

import { z } from "zod";

// --- Timestamp and format schemas ---

// Use for all machine-readable timestamps. Human-readable display strings
// (e.g., "2 hours ago", "Apr 21 - Apr 27") use z.string().min(1) instead.
export const TimestampSchema = z.string().datetime({ message: "Expected ISO 8601 datetime string" });

export const UrlSchema = z.string().url();

// --- Enum schemas (per RESEARCH.md Shared Types Catalog) ---

// Family: used in identity, home, memory, sources, map, pulse, chat, trust, bus
export const FamilySchema = z.enum(["skill", "tool", "topic", "pref"]);

// EntityId: format "family:kebab-name" (e.g., "skill:research")
// used in identity, home, memory, map, chat, bus
export const EntityIdSchema = z.string();

// SourceStatus: used in sources, trust
export const SourceStatusSchema = z.enum(["connected", "paused", "disconnected"]);

// SensitivityZone: used in memory, trust
export const SensitivityZoneSchema = z.enum(["public", "private", "secret"]);

// Trend: used in identity, pulse, insights
export const TrendSchema = z.enum(["up", "down", "stable"]);

// TimeGroup: used in memory
export const TimeGroupSchema = z.enum(["today", "yesterday", "thisWeek", "lastWeek", "older"]);

// SourceApp: app identifier string ("claude", "notion", etc.)
// used in sources, trust, apps, shell
export const SourceAppSchema = z.string();

// --- Compound schemas (all use .passthrough() per strictness policy) ---

// Entity: used in bus, map, chat
export const EntitySchema = z.object({
  fam: FamilySchema,
  name: z.string(),
}).passthrough();

// LabeledMetric: used in identity (COMM_DNA, WORK_RHYTHM)
export const LabeledMetricSchema = z.object({
  label: z.string(),
  value: z.string(),
  context: z.string(),
}).passthrough();

// MemoryBase: base memory type used in memory, sources, chat surfaces
export const MemoryBaseSchema = z.object({
  id: z.string(),
  fam: FamilySchema,
  text: z.string(),
  source: z.string(),
  time: z.string(), // Human-readable display string (e.g., "2 hours ago")
  tags: z.array(z.string()),
}).passthrough();

// --- Response envelope schemas -- DISCRIMINATED UNION PATTERN ---
// Per D-06: Three server states: ready (data present), error (failure), empty (no data yet).
// The backend never returns "loading" -- that is a frontend lifecycle state
// (the absence of a response, not a response variant). The server returns:
//   "ready"  -- data available
//   "error"  -- something failed
//   "empty"  -- no data yet / insufficient signals
// The client-side "loading" state is managed by the data-fetching layer
// (e.g., TanStack Query isLoading) before any server response arrives.

export const SectionReadinessSchema = z.object({
  ready: z.boolean(),
  signalStrength: z.number().min(0).max(1),
  message: z.string().nullish(),
}).passthrough();

export const SurfaceReadinessSchema = z.object({
  overall: z.number().min(0).max(1),
  sections: z.record(z.string(), SectionReadinessSchema),
  cachedAt: z.string().nullish(),
}).passthrough();

export const SurfaceErrorSchema = z.object({
  status: z.literal("error"),
  error: z.string().nullish(),
  code: z.string().nullish(),
  retryable: z.boolean().nullish(),
}).passthrough();

export const SurfaceEmptySchema = z.object({
  status: z.literal("empty"),
  message: z.string().nullish(),
  guidance: z.string().nullish(),
}).passthrough();

export const PaginationSchema = z.object({
  offset: z.number().int().min(0),
  limit: z.number().int().min(1),
  total: z.number().int().min(0),
  hasMore: z.boolean(),
}).passthrough();

/** Creates a discriminated response envelope for a surface. Per D-06.
 *  Three server states: ready (data present), error (failure), empty (no data yet).
 *  Client-side "loading" is the absence of a response, not a server state. */
export function surfaceEnvelope<T extends z.ZodTypeAny>(dataSchema: T) {
  const ready = z.object({ status: z.literal("ready"), data: dataSchema, readiness: SurfaceReadinessSchema }).passthrough();
  return z.discriminatedUnion("status", [ready, SurfaceErrorSchema, SurfaceEmptySchema]);
}

// --- Derived types (z.infer from each schema) ---

export type Timestamp = z.infer<typeof TimestampSchema>;
export type Url = z.infer<typeof UrlSchema>;
export type Family = z.infer<typeof FamilySchema>;
export type EntityId = z.infer<typeof EntityIdSchema>;
export type SourceStatus = z.infer<typeof SourceStatusSchema>;
export type SensitivityZone = z.infer<typeof SensitivityZoneSchema>;
export type Trend = z.infer<typeof TrendSchema>;
export type TimeGroup = z.infer<typeof TimeGroupSchema>;
export type SourceApp = z.infer<typeof SourceAppSchema>;
export type Entity = z.infer<typeof EntitySchema>;
export type LabeledMetric = z.infer<typeof LabeledMetricSchema>;
export type MemoryBase = z.infer<typeof MemoryBaseSchema>;
export type SectionReadiness = z.infer<typeof SectionReadinessSchema>;
export type SurfaceReadiness = z.infer<typeof SurfaceReadinessSchema>;
export type SurfaceError = z.infer<typeof SurfaceErrorSchema>;
export type SurfaceEmpty = z.infer<typeof SurfaceEmptySchema>;
export type Pagination = z.infer<typeof PaginationSchema>;
